import React, { useCallback } from "react";
import { GRID_CONFIG } from "@/three/config/grid";

// TopRightOverlay와 동일한 빈티지 문서 스타일
const VINTAGE_STYLES = {
  label: {
    color: "#666655",
    fontSize: "10px",
    fontWeight: 600 as const,
    textTransform: "uppercase" as const,
    letterSpacing: "1px",
    marginBottom: "4px",
  },
  button: {
    padding: "4px 10px",
    fontSize: "11px",
    fontWeight: 600,
    fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
    background: "#8b7355",
    border: "none",
    borderRadius: "3px",
    color: "#fff",
    cursor: "pointer",
  },
  buttonDanger: {
    background: "none",
    border: "none",
    color: "#c44",
    cursor: "pointer",
    fontSize: "14px",
    padding: "0 4px",
  },
  listItem: {
    padding: "6px 8px",
    borderBottom: "1px dashed #c4b9a0",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  slider: {
    flex: 1,
    height: "4px",
    cursor: "pointer",
  },
  value: {
    minWidth: "40px",
    textAlign: "right" as const,
    fontSize: "11px",
    color: "#2a2a2a",
    fontFamily: "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
  },
  axisLabel: {
    fontSize: "10px",
    fontWeight: 600,
    minWidth: "20px",
  },
};

interface EditorBox {
  id: string;
  position: [number, number, number];
  scale: [number, number, number];
}

interface EditorBoxListProps {
  boxes: EditorBox[];
  setBoxes: React.Dispatch<React.SetStateAction<EditorBox[]>>;
  selectedBoxId: string | null;
  setSelectedBoxId: React.Dispatch<React.SetStateAction<string | null>>;
}

const EDITOR_SIZE = GRID_CONFIG.CELL_SIZE - GRID_CONFIG.ROAD.WIDTH;
const HALF_SIZE = EDITOR_SIZE / 2;

/**
 * 빈티지 스타일 슬라이더
 */
function VintageSlider({
  label,
  value,
  min,
  max,
  onChange,
  color,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  color: string;
}) {
  const v = Math.max(min, Math.min(max, value));

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        marginBottom: "6px",
      }}
    >
      <span style={{ ...VINTAGE_STYLES.axisLabel, color }}>{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={0.05}
        value={v}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{
          ...VINTAGE_STYLES.slider,
          accentColor: color,
        }}
      />
      <span style={VINTAGE_STYLES.value}>{v.toFixed(2)}</span>
    </div>
  );
}

/**
 * 빈티지 문서 스타일 박스 목록 컴포넌트
 * TopRightOverlay와 동일한 디자인 시스템 사용
 */
export function EditorBoxList({
  boxes,
  setBoxes,
  selectedBoxId,
  setSelectedBoxId,
}: EditorBoxListProps) {
  const selectedBox = boxes.find((b) => b.id === selectedBoxId);

  const handleAddBox = useCallback(() => {
    const newBox: EditorBox = {
      id: `box-${Date.now()}`,
      position: [-HALF_SIZE, 0, -HALF_SIZE],
      scale: [0.3, 0.3, 0.3],
    };
    setBoxes((prev) => [...prev, newBox]);
    setSelectedBoxId(newBox.id);
  }, [setBoxes, setSelectedBoxId]);

  const handleDeleteBox = useCallback(
    (id: string) => {
      setBoxes((prev) => prev.filter((box) => box.id !== id));
      if (selectedBoxId === id) setSelectedBoxId(null);
    },
    [selectedBoxId, setBoxes, setSelectedBoxId]
  );

  const updatePos = useCallback(
    (axis: "x" | "y" | "z", value: number) => {
      if (!selectedBoxId) return;
      setBoxes((prev) =>
        prev.map((box) => {
          if (box.id !== selectedBoxId) return box;
          const newPos = [...box.position] as [number, number, number];
          if (axis === "x") newPos[0] = value;
          else if (axis === "y") newPos[1] = value;
          else newPos[2] = value;
          return { ...box, position: newPos };
        })
      );
    },
    [selectedBoxId, setBoxes]
  );

  const updateScale = useCallback(
    (axis: "x" | "y" | "z", value: number) => {
      if (!selectedBoxId) return;
      setBoxes((prev) =>
        prev.map((box) => {
          if (box.id !== selectedBoxId) return box;
          const newScale = [...box.scale] as [number, number, number];
          if (axis === "x") newScale[0] = value;
          else if (axis === "y") newScale[1] = value;
          else newScale[2] = value;
          return { ...box, scale: newScale };
        })
      );
    },
    [selectedBoxId, setBoxes]
  );

  return (
    <div
      style={{
        fontFamily:
          "'Pretendard GOV Variable','Noto Serif KR', 'Georgia', serif",
      }}
    >
      {/* 헤더 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <div style={VINTAGE_STYLES.label}>구조물 ({boxes.length})</div>
        <button onClick={handleAddBox} style={VINTAGE_STYLES.button}>
          + 추가
        </button>
      </div>

      {/* 목록 */}
      <div
        style={{
          maxHeight: "80px",
          overflowY: "auto",
          background: "#faf8f0",
          border: "1px solid #d4c9b0",
          borderRadius: "3px",
          marginBottom: "10px",
        }}
      >
        {boxes.length === 0 ? (
          <div
            style={{
              padding: "12px",
              textAlign: "center",
              color: "#888877",
              fontSize: "12px",
              fontStyle: "italic",
            }}
          >
            구조물이 없습니다
          </div>
        ) : (
          boxes.map((box, index) => (
            <div
              key={box.id}
              onClick={() => setSelectedBoxId(box.id)}
              style={{
                ...VINTAGE_STYLES.listItem,
                background:
                  selectedBoxId === box.id ? "#e8e4d8" : "transparent",
                borderBottom:
                  index < boxes.length - 1 ? "1px dashed #c4b9a0" : "none",
              }}
            >
              <span
                style={{
                  color: "#8b7355",
                  fontWeight: 700,
                  fontSize: "11px",
                  minWidth: "24px",
                }}
              >
                {index + 1}.
              </span>
              <span style={{ flex: 1, color: "#2a2a2a", fontSize: "11px" }}>
                {box.scale[0].toFixed(1)} × {box.scale[1].toFixed(1)} ×{" "}
                {box.scale[2].toFixed(1)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteBox(box.id);
                }}
                style={VINTAGE_STYLES.buttonDanger}
              >
                ×
              </button>
            </div>
          ))
        )}
      </div>

      {/* 편집 영역 */}
      {selectedBox && (
        <div
          style={{
            background: "#faf8f0",
            border: "1px solid #d4c9b0",
            borderRadius: "3px",
            padding: "10px",
          }}
        >
          {/* 크기 */}
          <div style={{ marginBottom: "10px" }}>
            <div style={{ ...VINTAGE_STYLES.label, marginBottom: "6px" }}>
              크기
            </div>
            <VintageSlider
              label="W"
              value={selectedBox.scale[0]}
              min={0.1}
              max={HALF_SIZE - selectedBox.position[0]}
              onChange={(v) => updateScale("x", v)}
              color="#c44"
            />
            <VintageSlider
              label="H"
              value={selectedBox.scale[1]}
              min={0.1}
              max={5 - selectedBox.position[1]}
              onChange={(v) => updateScale("y", v)}
              color="#4a4"
            />
            <VintageSlider
              label="D"
              value={selectedBox.scale[2]}
              min={0.1}
              max={HALF_SIZE - selectedBox.position[2]}
              onChange={(v) => updateScale("z", v)}
              color="#44c"
            />
          </div>

          {/* 위치 */}
          <div>
            <div style={{ ...VINTAGE_STYLES.label, marginBottom: "6px" }}>
              위치
            </div>
            <VintageSlider
              label="X"
              value={selectedBox.position[0]}
              min={-HALF_SIZE}
              max={HALF_SIZE - selectedBox.scale[0]}
              onChange={(v) => updatePos("x", v)}
              color="#c44"
            />
            <VintageSlider
              label="Y"
              value={selectedBox.position[1]}
              min={0}
              max={5 - selectedBox.scale[1]}
              onChange={(v) => updatePos("y", v)}
              color="#4a4"
            />
            <VintageSlider
              label="Z"
              value={selectedBox.position[2]}
              min={-HALF_SIZE}
              max={HALF_SIZE - selectedBox.scale[2]}
              onChange={(v) => updatePos("z", v)}
              color="#44c"
            />
          </div>
        </div>
      )}
    </div>
  );
}
